import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import BaseStockChart from "./BaseStockChart";
import { ChartDataService } from "../../services/ChartDataService";
import { useSAPDataStore } from "../../store/SAPDataStore";
import { useChartCache } from "../../hooks/useChartCache";
import { useThemedCharts } from "../../hooks/useThemedCharts";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";
import {
  usePresetMarkers,
  generatePresetPlotLines,
  generatePresetChartEvents,
  enhanceTooltipWithPresets,
  PresetToggle,
} from "./PresetMarkers";

interface EfficiencyStockChartProps {
  machineSerialNo?: string;
  lineId?: string;
  shifts?: string[];
  className?: string;
  syncGroup?: string;
  activeTab?: "efficiency" | "utilization";
}

/**
 * Top of the percentage axis, and the cap applied to plotted values.
 *
 * Efficiency/utilization genuinely exceed 100% in the raw data (one shift on
 * machine 22547 reads 1270%, and the worst row across all machines is in the
 * millions). Plotting those at face value flattens every normal bar, so the
 * drawn `y` is capped here while the real number travels on the point as
 * `custom.actual` for the tooltip and CSV export.
 *
 * Capping rather than letting the axis clip is deliberate: Highcharts gives a
 * point outside the axis range no hover target, so clipped outliers showed no
 * tooltip at all.
 */
const Y_CLAMP = 100;

/**
 * Epoch ms for LOCAL midnight of a "YYYY-MM-DD" date string.
 *
 * `new Date("2026-07-04")` is specified to parse as UTC midnight, which lands
 * the column away from its own tick on a locally-rendered time axis. Building
 * the date from its parts keeps the bucket on the tick, so the crosshair,
 * tooltip and drawn column all agree.
 */
function localMidnight(dateStr: string): number {
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return new Date(dateStr).getTime();
  return new Date(+m[1], +m[2] - 1, +m[3]).getTime();
}

interface EfficiencyDataPoint {
  date: string;
  efficiency: number;
  utilization: number;
  shift: string;
}

const EfficiencyStockChart: React.FC<EfficiencyStockChartProps> = ({
  machineSerialNo,
  shifts = [],
  className = "",
  syncGroup,
  activeTab: externalActiveTab,
}) => {
  const { t: _t } = useTranslation();
  const { token } = useUnifiedAuth();
  const { dateRange, getMachineById, getTargetForMachine } = useSAPDataStore();
  const { getStatusColors } = useThemedCharts();
  const [rawChartData, setRawChartData] = useState<EfficiencyDataPoint[]>([]);
  const [showPresetMarkers, setShowPresetMarkers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use external activeTab if provided, otherwise default to efficiency
  const activeTab = externalActiveTab ?? "efficiency";

  // Use the preset markers hook
  const { presetData } = usePresetMarkers(machineSerialNo, showPresetMarkers);

  // Use chart cache for better performance
  const chartCache = useChartCache<EfficiencyDataPoint[]>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 40, // Cache up to 40 different chart configurations
  });

  // Fetch efficiency and utilization data from NATS
  const fetchChartData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setRawChartData([]);
      setLoading(false);
      return;
    }

    if (!machineSerialNo || !dateRange.start || !dateRange.end) {
      return;
    }

    if (!token) {
      setError("Authentication required");
      return;
    }

    // Check cache first for combined efficiency data
    const cachedData = chartCache.getCachedData(
      "pc.efficiency+utilization",
      machineSerialNo,
      dateRange,
      shifts,
    );

    if (cachedData) {
      setRawChartData(cachedData);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(
        machineSerialNo,
        dateRange,
        shifts,
      );

      // Fetch both efficiency and utilization data
      const {
        efficiency: efficiencyResponse,
        utilization: utilizationResponse,
      } = await ChartDataService.fetchEfficiencyAndUtilization(params, token);

      // Process both responses
      const processedData: EfficiencyDataPoint[] = [];
      const efficiencyData: Record<string, Record<string, number>> = {};
      const utilizationData: Record<string, Record<string, number>> = {};

      // Process efficiency response
      if (efficiencyResponse.success && efficiencyResponse.data) {
        const responseData = efficiencyResponse.data as any;
        // Backend returns { status, results, error } - extract results
        const efficiencyDetail =
          responseData.results ||
          responseData.efficiency_detail ||
          responseData;

        console.log("🔍 Processing efficiency data:", {
          hasDetail: !!efficiencyDetail,
          detailType: typeof efficiencyDetail,
          keys:
            efficiencyDetail && typeof efficiencyDetail === "object"
              ? Object.keys(efficiencyDetail).slice(0, 5)
              : "N/A",
        });

        if (efficiencyDetail && typeof efficiencyDetail === "object") {
          Object.assign(efficiencyData, efficiencyDetail);
          console.log(
            "✅ Efficiency data assigned:",
            Object.keys(efficiencyData).length,
            "shifts",
          );
        }
      }

      // Process utilization response
      if (utilizationResponse.success && utilizationResponse.data) {
        const responseData = utilizationResponse.data as any;
        // Backend returns { status, results, error } - extract results
        const utilizationDetail =
          responseData.results ||
          responseData.utilization_detail ||
          responseData.efficiency_detail ||
          responseData;

        console.log("🔍 Processing utilization data:", {
          hasDetail: !!utilizationDetail,
          detailType: typeof utilizationDetail,
          keys:
            utilizationDetail && typeof utilizationDetail === "object"
              ? Object.keys(utilizationDetail).slice(0, 5)
              : "N/A",
        });

        if (utilizationDetail && typeof utilizationDetail === "object") {
          Object.assign(utilizationData, utilizationDetail);
          console.log(
            "✅ Utilization data assigned:",
            Object.keys(utilizationData).length,
            "shifts",
          );
        }
      }

      // Combine data from both responses - using the same logic as original EfficiencyChart
      const allShifts = new Set([
        ...Object.keys(efficiencyData),
        ...Object.keys(utilizationData),
      ]);

      console.log("🔄 Processing shifts and timestamps:", {
        allShifts: Array.from(allShifts),
        efficiencyDataKeys: Object.keys(efficiencyData),
        utilizationDataKeys: Object.keys(utilizationData),
        selectedShifts: shifts,
      });

      allShifts.forEach((shift) => {
        const effShiftData = efficiencyData[shift] || {};
        const utilShiftData = utilizationData[shift] || {};

        console.log(`📊 Processing shift ${shift}:`, {
          effTimestamps: Object.keys(effShiftData).length,
          utilTimestamps: Object.keys(utilShiftData).length,
        });

        // Get all timestamps for this shift from both datasets
        const allTimestamps = new Set([
          ...Object.keys(effShiftData),
          ...Object.keys(utilShiftData),
        ]);

        allTimestamps.forEach((timestamp) => {
          // Backend sends wall-clock shift times, e.g.
          // "2016-05-22 07:30:00 +0000 UTC". Take the calendar date straight
          // from the string instead of round-tripping through Date: parsing a
          // zone-less string yields LOCAL time while toISOString() serialises
          // UTC, so the two together shifted the day for any browser whose
          // offset pushed the shift time across midnight.
          const cleanTimestamp = timestamp
            .replace(" +0000 UTC", "")
            .replace(" UTC", "");
          const date = cleanTimestamp.split(/[T ]/)[0];

          const rawEfficiency = effShiftData[timestamp] || 0;
          const rawUtilization = utilShiftData[timestamp] || 0;
          const efficiency =
            typeof rawEfficiency === "number"
              ? rawEfficiency
              : parseFloat(rawEfficiency) || 0;
          const utilization =
            typeof rawUtilization === "number"
              ? rawUtilization
              : parseFloat(rawUtilization) || 0;

          // Only include if in selected shifts or no shifts filter
          if (shifts.length === 0 || shifts.includes(shift)) {
            processedData.push({
              date,
              efficiency,
              utilization,
              shift,
            });
          }
        });
      });

      // Sort by date
      processedData.sort(
        (a, b) => localMidnight(a.date) - localMidnight(b.date),
      );

      console.log("📊 EfficiencyStockChart: Processed data:", {
        totalPoints: processedData.length,
        dateRange:
          processedData.length > 0
            ? {
                from: processedData[0].date,
                to: processedData[processedData.length - 1].date,
              }
            : null,
        sampleData: processedData.slice(0, 3),
        allShifts: [...new Set(processedData.map((p) => p.shift))],
        dataStructure: processedData.slice(0, 2).map((p) => ({
          date: p.date,
          efficiency: p.efficiency,
          utilization: p.utilization,
          shift: p.shift,
        })),
      });

      setRawChartData(processedData);

      // Cache the successful response
      chartCache.setCachedData(
        "pc.efficiency+utilization",
        machineSerialNo,
        dateRange,
        shifts,
        processedData,
      );

      console.log("📊 EfficiencyStockChart: Final processed data summary:", {
        totalPoints: processedData.length,
        firstFewPoints: processedData.slice(0, 5),
        shifts: [...new Set(processedData.map((p) => p.shift))],
        dateRange:
          processedData.length > 0
            ? {
                from: processedData[0].date,
                to: processedData[processedData.length - 1].date,
              }
            : null,
      });

      // Check if we have any errors from either response
      const errors = [];
      if (efficiencyResponse.error)
        errors.push(`Efficiency: ${efficiencyResponse.error}`);
      if (utilizationResponse.error)
        errors.push(`Utilization: ${utilizationResponse.error}`);

      if (errors.length > 0) {
        setError(errors.join("; "));
      }
    } catch (error) {
      console.error("❌ EfficiencyStockChart: Failed to fetch data:", error);
      setError(error instanceof Error ? error.message : "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when dependencies change
  useEffect(() => {
    fetchChartData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts, activeTab]);

  // Convert data to Highstock format with timestamps
  const stockChartProps = useMemo(() => {
    const isEfficiency = activeTab === "efficiency";
    const dataKey = isEfficiency ? "efficiency" : "utilization";

    // Group data by shift to create series
    const shiftGroups = rawChartData.reduce(
      (acc, item) => {
        if (!acc[item.shift]) {
          acc[item.shift] = [];
        }
        acc[item.shift].push(item);
        return acc;
      },
      {} as Record<string, EfficiencyDataPoint[]>,
    );

    // Create series for each shift with timestamp data
    const series = Object.keys(shiftGroups).map((shift) => {
      const shiftData = shiftGroups[shift];

      // Convert to [timestamp, value] format for Highstock
      const seriesData = shiftData
        .map((item) => {
          // Anchor each day bucket to LOCAL midnight. `new Date("2026-07-04")`
          // would give UTC midnight, but the chart's time zone is the browser's
          // local one (set in main.tsx), so the column landed hours away from
          // its own axis tick — the crosshair/tooltip then read one position
          // while the bar was drawn at another.
          const timestamp = localMidnight(item.date);
          const rawValue = item[dataKey];
          // Ensure numeric value - parse string if needed
          const value =
            typeof rawValue === "number"
              ? rawValue
              : parseFloat(String(rawValue)) || 0;
          // Cap the PLOTTED value at the axis top and carry the real figure in
          // `custom.actual`. A point outside the axis range is clipped by
          // Highcharts and gets no hover target, so an over-100% shift showed no
          // tooltip at all unless another in-range series happened to trigger
          // the shared one. Capping keeps every point hoverable; the tooltip
          // below reports `custom.actual`, never the capped `y`.
          return {
            x: timestamp,
            y: Math.min(value, Y_CLAMP),
            custom: { actual: value },
          };
        })
        .filter((p) => !isNaN(p.x) && p.y !== null && !isNaN(p.y))
        .sort((a, b) => a.x - b.x); // Sort by timestamp

      return {
        name: `Shift ${shift}`,
        data: seriesData,
        // Color will be automatically assigned by BaseStockChart using default color palette
      };
    });

    return {
      series,
      title: isEfficiency ? "Efficiency Over Time" : "Utilization Over Time",
      subtitle: `${machineSerialNo || "No machine selected"} • ${series.length} shift${series.length !== 1 ? "s" : ""}`,
      yAxisTitle: isEfficiency ? "Efficiency (%)" : "Utilization (%)",
    };
  }, [rawChartData, activeTab, machineSerialNo]);

  // Calculate dynamic target for the current machine
  const dynamicTarget = useMemo(() => {
    if (!machineSerialNo) return null;

    const machine = getMachineById(machineSerialNo);
    if (!machine) return null;

    // Get target based on current active tab
    const targetType =
      activeTab === "efficiency" ? "efficiency" : "utilization";
    return getTargetForMachine(machine, targetType);
  }, [machineSerialNo, getMachineById, getTargetForMachine, activeTab]);

  // Debug log the chart props
  console.log("🔍 EfficiencyStockChart: Chart props:", {
    rawDataLength: rawChartData.length,
    seriesCount: stockChartProps.series.length,
    seriesData: stockChartProps.series.map((s) => ({
      name: s.name,
      dataLength: s.data.length,
      sampleData: s.data.slice(0, 3),
    })),
    activeTab,
    sampleRawData: rawChartData.slice(0, 3),
  });

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Stock Chart */}
      <BaseStockChart
        commentProps={{ chartType: "efficiency", machineBK: machineSerialNo }}
        type="column"
        height={500}
        title={stockChartProps.title}
        subtitle={stockChartProps.subtitle}
        series={stockChartProps.series}
        tooltip={{ suffix: "%" }}
        yAxis={{
          // Fixed 0–100% scale so one outlier can't flatten every normal bar.
          // Safe as a hard max because the plotted `y` is capped to it (see
          // Y_CLAMP above) — nothing is drawn out of range, so every point keeps
          // its hover target. The tooltip reports the uncapped value.
          min: 0,
          max: Y_CLAMP,
          endOnTick: false,
          startOnTick: false,
          format: "{value}%",
          title: stockChartProps.yAxisTitle,
          plotLines:
            dynamicTarget !== null
              ? [
                  {
                    value: dynamicTarget,
                    color: getStatusColors.plotLine,
                    dashStyle: "Dash" as const,
                    width: 2,
                    label:
                      activeTab === "efficiency"
                        ? "Efficiency Target"
                        : "Utilization Target",
                  },
                ]
              : [],
        }}
        customOptions={{
          chart: {
            events: generatePresetChartEvents(),
          },
          xAxis: {
            plotLines: generatePresetPlotLines(presetData, showPresetMarkers),
          },
          tooltip: {
            // Render into a detached div instead of the chart's SVG. An outlier
            // above 100% becomes the axis maximum, so its bar tops out at the
            // very edge of the plot and the tooltip — anchored above it — was
            // clipped out of view whenever it was the only point at that x.
            outside: true,
            formatter: enhanceTooltipWithPresets(
              function (this: any) {
                let tooltip = `<b>${new Date(this.x).toLocaleDateString()}</b><br/>`;

                // `this.points` only exists for the shared context. When just
                // one series has a value at this x, Highcharts passes the single
                // point instead — without this fallback the tooltip rendered as
                // a bare date with no value.
                const points =
                  this.points && this.points.length > 0
                    ? this.points
                    : this.point
                      ? [this.point]
                      : [];

                for (const point of points) {
                  // `y` is capped at Y_CLAMP so the bar stays inside the axis;
                  // the uncapped figure rides on the point as `custom.actual`.
                  const actual =
                    point.options?.custom?.actual ??
                    point.custom?.actual ??
                    point.y;
                  const value =
                    typeof actual === "number" ? actual.toFixed(1) : actual;
                  const capped = typeof actual === "number" && actual > Y_CLAMP;
                  tooltip +=
                    `<span style="color:${point.series.color}">●</span> ${point.series.name}: <b>${value}%</b>` +
                    (capped
                      ? ` <span style="opacity:0.7">(bar capped at ${Y_CLAMP}%)</span>`
                      : "") +
                    "<br/>";
                }

                return tooltip;
              },
              presetData,
              showPresetMarkers,
            ),
          },
        }}
        loading={loading}
        error={error}
        onRetry={fetchChartData}
        legend={{ enabled: true, position: "bottom" }}
        panZoom={{
          enabled: true,
          type: "x",
          mouseWheel: true,
          pinchZoom: true,
        }}
        navigator={{
          enabled: true,
          height: 80,
          // Navigator colors will be automatically themed by BaseStockChart
        }}
        rangeSelector={{
          enabled: false,
        }}
        onRangeChange={(min, max) => {
          console.log("Range changed:", {
            from: new Date(min).toISOString(),
            to: new Date(max).toISOString(),
          });
        }}
        noDataMessage={
          !machineSerialNo
            ? "No machine selected"
            : !dateRange.start || !dateRange.end
              ? "Please select a date range"
              : !shifts || shifts.length === 0
                ? "No shifts selected or available for the selected timeframe"
                : activeTab === "utilization"
                  ? "No utilization data found for the selected period"
                  : "No efficiency data found for the selected period"
        }
        syncGroup={syncGroup}
        extraControls={
          <PresetToggle
            enabled={showPresetMarkers}
            onToggle={setShowPresetMarkers}
            presetCount={presetData.length}
          />
        }
      />
    </div>
  );
};

export default EfficiencyStockChart;
